import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * One pagination control (replaces the 3 divergent variants). Wraps so number rows never
 * overflow on mobile.
 *
 * <Pagination page={page} pages={pages} total={total} pageSize={50} onPageChange={setPage} />
 *
 * props: page, pages, total?, pageSize?, onPageChange, mode? 'compact'(default)|'numbered'
 */
function windowed(page, pages) {
  const out = [];
  const add = (v) => out.push(v);
  add(1);
  for (let p = page - 1; p <= page + 1; p++) if (p > 1 && p < pages) add(p);
  if (pages > 1) add(pages);
  const uniq = [...new Set(out)].sort((a, b) => a - b);
  const withGaps = [];
  uniq.forEach((p, i) => {
    if (i > 0 && p - uniq[i - 1] > 1) withGaps.push("…");
    withGaps.push(p);
  });
  return withGaps;
}

export function Pagination({ page, pages, total, pageSize, onPageChange, mode = "compact", className }) {
  if (!pages || pages <= 1) {
    if (!total) return null;
  }
  const go = (p) => p >= 1 && p <= pages && p !== page && onPageChange?.(p);

  const rangeText =
    total != null && pageSize != null
      ? `Showing ${Math.min((page - 1) * pageSize + 1, total)}–${Math.min(page * pageSize, total)} of ${total}`
      : `Page ${page} of ${pages}`;

  return (
    <div className={cn("flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between", className)}>
      <p className="text-sm text-slate-500">{rangeText}</p>
      <div className="flex items-center flex-wrap gap-1">
        <Button variant="outline" size="sm" onClick={() => go(page - 1)} disabled={page <= 1}>
          <ChevronLeft className="h-4 w-4" />
          <span className="ml-1 hidden sm:inline">Prev</span>
        </Button>

        {mode === "numbered" ? (
          windowed(page, pages).map((p, i) =>
            p === "…" ? (
              <span key={`g${i}`} className="px-2 text-slate-400">
                …
              </span>
            ) : (
              <Button
                key={p}
                size="sm"
                variant={p === page ? "default" : "outline"}
                onClick={() => go(p)}
                className="min-w-9"
              >
                {p}
              </Button>
            )
          )
        ) : (
          <span className="px-2 text-sm text-slate-600 tabular-nums">
            {page} / {pages}
          </span>
        )}

        <Button variant="outline" size="sm" onClick={() => go(page + 1)} disabled={page >= pages}>
          <span className="mr-1 hidden sm:inline">Next</span>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export default Pagination;
